import { extendTheme } from "@chakra-ui/react";

/* 
 * This page extends on the Chakra UI Theme. 
 * It allows you to put custom elements and override existing Chakra UI Components.
*/

const theme = extendTheme({
    radii: {
        sm: '0.4rem',
        base: 'null',
        md: '0.8rem',
        lg: '1.6rem',
        xl: 'null',
        '2xl': 'null',
        '3xl': 'null',
        full: '9999rem',
    },
    fontSizes: {
        xs: "1.2rem",
        sm: "1.3rem",
        md: "1.4rem",
        lg: "1.6rem",
        xl: "1.8rem",
        "2xl": "2.4rem",
        "3xl": "3.0rem",
        "4xl": "3.6rem",
        "5xl": "4.2rem",
        "6xl": "4.8rem",
        "7xl": "5.4rem",
        "8xl": "6.0rem",
        "9xl": "6.4rem",
    },
    lineHeights: {
        normal: "1.6",
    },
    fonts: {
        body: "Open Sans, sans-serif",
        heading: "Soho-Regular, serif",
        mono: "Courier, monospace",
    },
    space: {
        px: '1px',
        0.5: '0.4rem',
        1: '0.8rem',
        1.5: 'null',
        2: '1.6rem',
        2.5: 'null',
        3: '2.4rem',
        3.5: 'null',
        4: '3.2rem',
        5: '4.0rem',
        6: '4.8rem',
        7: '5.6rem',
        8: '6.4rem',
        9: '7.2rem',
        10: '8.0rem',
        11: '8.8rem', 
        12: '9.6rem',
        13: '10.4rem', 
        14: '11.2rem',
        15: '12.0rem',
        16: '12.8rem',
        17: '13.6rem', 
        18: '14.4rem',
        19: '15.2rem', 
        20: '16.0rem',
        24: 'null',
        28: 'null',
        32: 'null',
        36: 'null',
        40: 'null',
        44: 'null',
        48: 'null',
        52: 'null',
        56: 'null',
        60: 'null',
        64: 'null',
        72: 'null',
        80: 'null',
        96: 'null',
    },
    shadows: {
        xs: 0,
        sm: '0px 1px 2px rgba(60, 60, 120, 0.12), 0px 2px 3px rgba(60, 60, 120, 0.06), 0px 2px 6px rgba(60, 60, 120, 0.08)',
        base: 0,
        md: '0px 1px 4px rgba(60, 60, 120, 0.12), 0px 0px 8px rgba(60, 60, 120, 0.09), 0px 2px 12px rgba(60, 60, 120, 0.06)',
        lg: '0px 2px 12px rgba(60, 60, 120, 0.09), 0px 2px 18px rgba(60, 60, 120, 0.09), 0px 12px 30px rgba(60, 60, 120, 0.06)',
        xl: '0px 0px 12px rgba(60, 60, 120, 0.04), 0px 0px 24px rgba(60, 60, 120, 0.04), 0px 12px 42px rgba(60, 60, 120, 0.04)',
    },
    breakpoints: {
        sm: '76.8rem',
        md: '99.2rem',
        lg: '120rem',
        xl: '192rem',
        '2xl': '300rem',
    },
    colors: {
        whiteAlpha: {
            50: 'null',
            100: 'hsla(0deg, 0%, 100%, 0.25)',
            200: 'hsla(0deg, 0%, 100%, 0.45)',
            300: 'hsla(0deg, 0%, 100%, 0.60)',
            400: 'null',
            500: 'null',
            600: 'null',
            700: 'null',
            800: 'null',
            900: 'null',
        },
        blackAlpha: {
            50: 'null',
            100: 'hsla(200deg, 100%, 6%, 0.12)',
            200: 'hsla(200deg, 100%, 6%, 0.30)',
            300: 'hsla(200deg, 100%, 3%, 0.50)',
            400: 'null',
            500: 'null',
            600: 'null',
            700: 'null',
            800: 'null',
            900: 'null',
        },
        gray: {
            50: 'null',
            100: '#F1F4F5',
            200: '#dee2e3',
            300: '#CBD6DB',
            400: '#A6B8C0',
            500: '#819AA5',
            600: '#5D7B89',
            700: '#395D6D',
            800: '#263f49',
            900: '#152329',
        },
        blue: {
            50: 'null',
            100: '#E9F8FB',
            200: '#7CD8FF',
            300: '#42C6FF',
            400: '#14B9FF',
            500: '#00A1E6',
            600: '#0081B8',
            700: '#005073',
            800: '#003045',
            900: '#001822',
        },
        green: {
            50: 'null',
            100: '#D9F2B6',
            200: '#C5EB8F',
            300: '#ACE35F',
            400: '#97DC38',
            500: '#80C322',
            600: '#679C1C',
            700: '#406211',
            800: '#263B0A',
            900: '#131D05',
        },
        red: {
            50: 'null',
            100: '#EEB1B4',
            200: '#E68C91',
            300: '#DC5E65',
            400: '#D43A41',
            500: '#B82830',
            600: '#932026',
            700: '#5C1418',
            800: '#4A1013',
            900: '#370C0E',
        },
        orange: {
            50: 'null',
            100: '#FDE6C9',
            200: '#FCD3A1',
            300: '#FBC787',
            400: '#F9AE52',
            500: '#F7951D',
            600: '#D57808',
            700: '#854B05',
            800: '#6A3C04',
            900: '#502D03',
        },
        purple: {
            50: 'null',
            100: '#E9E2F5',
            200: '#CBBCE9',
            300: '#A68CD9',
            400: '#8866CC',
            500: '#6A40BF',
            600: '#553399',
            700: '#352060',
            800: '#201339',
            900: '#100A1D',
        },
        yellow: {
            50: 'null',
            100: '#F9F9C6',
            200: '#F6F6AF',
            300: '#F0F075',
            400: '#EBEB47',
            500: '#E6E61A',
            600: '#B8B814',
            700: '#73730D',
            800: '#454508',
            900: '#2E2E05',
        }
    },
    components: {
        Button: {
            variants: {
                Primary: {
                    bgGradient: 'linear(gray.800 0% ,black 70%)',
                    color: 'white',
                    variant: 'solid',
                    _hover: { bgGradient: 'linear(black 0% ,black 100%)' }
                },
                Secondary: {
                    backgroundColor: 'transparent',
                    color: 'grey.900',
                    border: '1px',
                    borderColor: 'blue.500',
                    _hover: {
                        borderColor: 'blue.600',
                        color: 'black'
                    }
                },
                Light: {
                    backgroundColor: 'transparent',
                    border: '1px',
                    borderColor: 'rgba(21, 35, 41, 0.10)',
                    color: 'grey.900',
                    _hover: {
                        color: 'black',
                        borderColor: 'rgba(21, 35, 41, 0.20)'
                    }
                },
                Clear: {
                    backgroundColor: 'transparent',
                    color: 'rgb(80, 90, 94)',
                    _hover: {
                        color: 'black'
                    }
                },
                White : {
                    backgroundColor: 'transparent',
                    shadow: 'sm',
                    color: 'grey.900',
                    _hover: {
                        color: 'black',
                        shadow: 'md'
                    }
                },
                Delete : {
                    backgroundColor: 'red.100',
                    color: 'red.800',
                    _hover: { 
                        backgroundColor: 'red.200',
                        color: 'red.900'
                    }
                },
                Cancel : {
                    backgroundColor: 'red.500',
                    color: 'white',
                    _hover: { 
                        backgroundColor: 'red.700',
                        color: 'white'
                    }
                },
            }
        }, 
        Modal: {
            baseStyle: {
                overlay: {
                    backgroundImage: "linear-gradient(90deg, rgba(26,26,33,0.62) 0%, rgba(26,26,33,0.62) 0%)",
                    background: "linear-gradient(90deg, rgba(26,26,33,0.62) 0%, rgba(26,26,33,0.62) 0%)",
                    backgroundColor: "linear-gradient(90deg, rgba(26,26,33,0.62) 0%, rgba(26,26,33,0.62) 0%)"
                }
            }
        }
    }
});

export default theme;